import React, { useState, useEffect } from 'react';
import { Globe, Check, Copy, AlertTriangle, Trash2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}` });

export default function EmailDomainPanel() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [freeMailbox, setFreeMailbox] = useState(false);
  const [copied, setCopied] = useState('');

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/email-domain`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const setup = async () => {
    setBusy(true); setError(''); setFreeMailbox(false);
    try {
      const res = await fetch(`${API_URL}/api/settings/email-domain`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender_email: email.trim() })
      });
      const d = await res.json();
      if (res.ok) setData(d);
      else if (d.detail === 'free_mailbox') setFreeMailbox(true);
      else setError(d.detail || 'Something went wrong');
    } catch (e) { setError('Could not reach the server'); }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/settings/email-domain/verify`, { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      if (res.ok) {
        setData(prev => ({ ...prev, verified: d.verified, status: d.status, records: d.records?.length ? d.records : prev.records }));
        if (!d.verified) setError('Not verified yet — DNS changes can take up to an hour. Try again shortly.');
      } else setError(d.detail || 'Verification failed');
    } catch (e) { setError('Could not reach the server'); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Remove your custom email domain? Emails will send from the StreakFree address instead.')) return;
    setBusy(true);
    await fetch(`${API_URL}/api/settings/email-domain`, { method: 'DELETE', headers: authHeaders() });
    setData({ configured: false });
    setEmail('');
    setBusy(false);
  };

  if (!data) return null;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3" data-testid="email-domain-panel">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
          <Globe size={18} className="text-sky-600" />
        </div>
        <div>
          <p className="font-medium text-zinc-900">Email Sending Domain</p>
          <p className="text-xs text-zinc-500">Send invoices & reminders from your own email address, so customers see your business — not ours.</p>
        </div>
      </div>

      {!data.configured && (
        <>
          <div className="space-y-1.5" data-testid="email-domain-steps">
            {[
              <>Type the address you email customers from, e.g. <span className="font-mono">info@yourbusiness.co.uk</span></>,
              <>We'll show you <strong>3 DNS records</strong> to add where your domain is hosted (GoDaddy, 123-Reg, Cloudflare…)</>,
              <>Tap <strong>Verify</strong> — once green, every email sends from your address and replies come to you</>,
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-xs text-zinc-600 leading-relaxed">{s}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="info@yourbusiness.co.uk"
              className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm"
              data-testid="email-domain-input"
            />
            <button
              onClick={setup}
              disabled={busy || !email.includes('@')}
              className="px-4 py-2.5 rounded-xl bg-zinc-900 text-white text-sm font-bold disabled:bg-zinc-300"
              data-testid="email-domain-setup-btn"
            >
              {busy ? '…' : 'Set up'}
            </button>
          </div>
          {freeMailbox && (
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl text-xs text-sky-900 leading-relaxed" data-testid="email-domain-free-mailbox">
              <strong>Gmail / Hotmail addresses can't be verified</strong> (only domains you own can). No problem though — your emails already send with your business name showing, and replies go straight to your inbox. Nothing to set up.
            </div>
          )}
        </>
      )}

      {data.configured && !data.verified && (
        <div className="space-y-2" data-testid="email-domain-records">
          <p className="text-xs text-zinc-600">Add these records where <strong>{(data.sender_email || '').split('@')[1]}</strong> is hosted, then tap Verify:</p>
          {(data.records || []).map((r, i) => (
            <div key={i} className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-[11px] font-mono space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-zinc-700">{r.type}{r.priority != null ? ` (priority ${r.priority})` : ''}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-sans font-bold ${r.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status === 'verified' ? 'OK' : 'pending'}</span>
              </div>
              {[['Name', r.name, `n${i}`], ['Value', r.value, `v${i}`]].map(([label, val, key]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className="text-zinc-400 w-10 flex-shrink-0 font-sans">{label}</span>
                  <span className="truncate flex-1 text-zinc-700">{val}</span>
                  <button onClick={() => copy(val, key)} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0">
                    {copied === key ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
                  </button>
                </div>
              ))}
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={verify} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold disabled:bg-zinc-300 flex items-center justify-center gap-2" data-testid="email-domain-verify-btn">
              <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Verify
            </button>
            <button onClick={remove} disabled={busy} className="px-3 py-2.5 rounded-xl border border-zinc-200 text-zinc-400" data-testid="email-domain-remove-btn">
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      )}

      {data.configured && data.verified && (
        <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="email-domain-verified">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center"><Check size={13} className="text-white" strokeWidth={3} /></div>
            <p className="text-sm font-semibold text-emerald-900">Emails send from {data.sender_email}</p>
          </div>
          <button onClick={remove} disabled={busy} className="text-zinc-400 hover:text-red-500" data-testid="email-domain-remove-btn">
            <Trash2 size={15} />
          </button>
        </div>
      )}

      {error && <p className="text-xs text-amber-700 flex items-start gap-1.5" data-testid="email-domain-error"><AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />{error}</p>}
    </div>
  );
}
